import { AfterViewInit, Component } from "@angular/core";
import { AssetImagesService } from "./service/image-assets.service";
import { AssetImageViewModel, AssetImagesSearchParams } from "./model/image-assets.model";
import { DataElement, PaginationParams } from "@shared/models/common";
import { NgRedux } from "@shared/redux/redux-compatibility.service";
import { AppState } from "@shared/redux/store";
import { TabIdList } from "@shared/models/tab.model";
import { Catalog } from "@shared/models/cat.model";
import { SharedService } from "@shared/services/shared.service";
import { TranslateService } from "@ngx-translate/core";
import { ImageAssetsMetadataHandler } from "./service/image-assets-metadata.handler";

@Component({
    selector: 'image-assets',
    templateUrl: './image-assets.component.html',
    styleUrls: ['./image-assets.component.scss'],
    providers: [
        { provide: AssetImagesService, useClass: AssetImagesService },
        { provide: ImageAssetsMetadataHandler, useClass: ImageAssetsMetadataHandler }
    ],
    standalone: false
})
export class ImageAssetsComponent implements AfterViewInit {
    loading = false;
    loadingChanges = false;
    displayAssets: AssetImageViewModel[] = [];
    searchParams: AssetImagesSearchParams = new AssetImagesSearchParams();
    rowsPerPage = 50;
    totalRecords = 0;
    tabIdList: TabIdList | undefined = undefined;
    editMetadata = false;
    assetToEdit: AssetImageViewModel | null = null;
    filterAssetType: DataElement = null;
    filterCategory: DataElement = null;
    classificationCategories: DataElement[] = [];
    classificationCategoriesLoading = false;

    constructor(private assetImagesService: AssetImagesService,
                private ngRedux: NgRedux<AppState>,
                private docMetadataHandler: ImageAssetsMetadataHandler,
                private sharedService: SharedService,
                private translateService: TranslateService) {
        this.tabIdList = this.ngRedux.getState().ui_tabIdList;
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.preloadMetadataDefinitions();
            this.loadClassificationCategories();
            this.loadEntries(true);
        }, 0);
    }

    private loadClassificationCategories() {
        const fallback = ['Logo', 'Branding', 'Marketing', 'Lifestyle', 'People', 'Chart', 'Diagram', 'Other'];
        this.classificationCategoriesLoading = true;
        this.assetImagesService.getClassificationCategories(this.catId, this.envId, this.lang).subscribe({
            next: (cats) => {
                const list = cats && cats.length ? cats : fallback;
                this.classificationCategories = list.map(c => new DataElement(c, c));
                this.classificationCategoriesLoading = false;
            },
            error: () => {
                this.classificationCategories = fallback.map(c => new DataElement(c, c));
                this.classificationCategoriesLoading = false;
            }
        });
    }

    private preloadMetadataDefinitions() {
        if (this.envId && this.catVersionId && this.lang) {
            this.docMetadataHandler.getDocMetadataDefinition(this.envId, this.catVersionId, this.lang, null, 'CLAIM').subscribe({
                next: () => {},
                error: () => {}
            });
        }
    }

    get curCat(): Catalog | undefined { return this.ngRedux.getState().curCat; }
    get numberOfRows(): number { return this.displayAssets?.length || 0; }
    get catId(): number { return this.ngRedux.getState().curCat?.catId; }
    get catVersionId(): number { return this.ngRedux.getState().curCat?.catVersionId; }
    get envId(): number { return this.ngRedux.getState().curEnv?.id; }
    get catSystemVersion(): number { return this.ngRedux.getState().curCat?.systemVersion; }
    get lang(): string { return this.ngRedux.getState().ui_lang; }
    get figureTypeList(): DataElement[] {
        return [
            new DataElement('IMAGE', 'Image'),
            new DataElement('TABLE', 'Table'),
        ];
    }
    get displayClearFilter(): boolean { return this.filterAssetType != null || this.filterCategory != null; }
    onFieldValueChanged(e?: any) { if (e != null && e.originalEvent) (e.originalEvent as MouseEvent).stopPropagation(); this.loadEntries(true); }
    clearFilter() { this.filterAssetType = null; this.filterCategory = null; this.loadEntries(true); }

    loadEntries(newSearch: boolean) {
        let pageNum = 1;
        if (newSearch) {
            pageNum = 1;
            this.searchParams.rangeFrom = 0;
            this.searchParams.rangeTo = this.rowsPerPage;
        }
        else {
            if (this.searchParams.rangeFrom == undefined || this.searchParams.rangeFrom == this.totalRecords) return;
            pageNum = Math.floor(this.searchParams.rangeFrom / this.rowsPerPage) + 1;
        }
        this.loading = true;
        const figureType = this.filterAssetType?.id || undefined;
        const figureCategory = this.filterCategory?.id || undefined;
        this.assetImagesService.getAssetImages(this.catId, pageNum, this.rowsPerPage, figureType, figureCategory).subscribe((data) => {
            this.displayAssets = data.imageAssets;
            this.totalRecords = data.stats.totalCount;
            this.loading = false;
        }, () => {
            this.loading = false;
            this.displayAssets = [];
            this.totalRecords = 0;
        });
    }

    onPagination(event: PaginationParams) {
        if (event.rangeFrom == undefined || event.rangeTo == undefined) return;
        if (!this.displayAssets || (event.rangeFrom - 1 == this.searchParams.rangeFrom && event.rangeTo == this.searchParams.rangeTo)) return;
        this.searchParams.rangeFrom = event.rangeFrom - 1;
        this.searchParams.rangeTo = event.rangeTo;
        this.loadEntries(false);
    }

    edit(e: any, asset: AssetImageViewModel) {
        if (e) (e as MouseEvent).stopPropagation();
        this.assetToEdit = asset;
        (this.docMetadataHandler as any).assetDocMetadata = asset.metadata;
        this.editMetadata = true;
    }

    delete(e: any, asset: AssetImageViewModel) {
        if (e) (e as MouseEvent).stopPropagation();
        let validationStr = this.translateService.instant('translate.cat.deleteAsset');
        this.sharedService.emitPopupchReceivedEvent({
            title: 'translate.validation.confirm',
            icon: 'exclamation-triangle',
            subTitle: validationStr,
            keep: false,
            approve: () => {
                this.loading = true;
                this.assetImagesService.deleteAssetImage(asset.imageAssetId).subscribe(() => {
                    this.loading = false;
                    this.displayAssets = this.displayAssets.filter(a => a.imageAssetId !== asset.imageAssetId);
                    this.totalRecords--;
                }, () => { this.loading = false; });
            },
            onClose: () => { this.loading = false; }
        });
    }

    getFieldLabel(fieldName: string): string {
        const groups: any[] = (this.docMetadataHandler as any).groups;
        if (groups) {
            const g = groups.find((gr: any) => gr.groupName === fieldName);
            if (g && g.groupLabel) return g.groupLabel;
        }
        return fieldName.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
    }

    displayValues(metadata: {fieldLabel: string; fieldName: string; fieldValues: string[]}) {
        const byGroup: any = (this.docMetadataHandler as any).docMetadataByGroup;
        if (byGroup && byGroup[metadata.fieldName]) {
            const list: any[] = byGroup[metadata.fieldName];
            const mapped = metadata.fieldValues.map((v: string) => {
                const found = list.find((m: any) => m.fieldValue == v || m.fieldLabel == v);
                return found ? found.fieldLabel : v;
            });
            return mapped.join(", ");
        }
        return metadata.fieldValues.join(", ");
    }

    saveMetadataChanges() {
        if (!this.assetToEdit) return;
        let metadataToAdd = this.docMetadataHandler.getDocMetadataToAdd();
        if (!metadataToAdd) return;
        if (Object.keys(metadataToAdd).length == 0) return;
        this.loadingChanges = true;
        this.assetImagesService.saveAssetImageMetadata(this.assetToEdit.imageAssetId, metadataToAdd).subscribe(() => {
            this.loadingChanges = false;
            this.editMetadata = false;
            if (this.assetToEdit) {
                this.assetToEdit.metadata = this.docMetadataHandler.updateClaimMetadata(metadataToAdd);
            }
        }, () => { this.loadingChanges = false; });
    }

    cancelEditMetadata() { this.editMetadata = false; }
    get enableSaveMetadataChanges(): boolean { return (this.docMetadataHandler as any).enableSaveMetadataChanges(); }
}
